import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import momentTimezone from 'moment-timezone';
import _ from 'lodash';
import { useQuery } from '@apollo/client';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { AUTHENTICATION_SCOPES, withTranslation } from '@playbookux/playbook-web-core';

import { signOut } from '../../../core/actions';

import {
  TIMEZONE_NAME,
} from '../../../core/components/timezone-dropdown/graphql';

import {
  FORMAT_DATE,
  FORMAT_TIME,
  INVITATION_TEXT_LABEL,
  LIVE_TEXT_LABEL,
  NO_RESCHEDULED_MESSAGE,
  RESCHEDULED_MESSAGE,
} from './constants';

import {
  Card,
  Container,
  Content,
  InvitationText,
  DateContainer,
  Link,
  Title,
} from './styles';

const Summary = (props) => {
  const {
    location: { state, data },
    authentication: { scope, profile },
    history,
    onSignOut,
    phrases,
  } = props;

  const contentRef = useRef(null);

  if (_.isEmpty(state)) {
    history.push('/');
  }

  const {
    timeZone,
    date,
    forceReschedule,
    originalTestSession = {},
    projectId = null,
  } = state || {};
  const {
    createScheduledTester = {},
  } = data || {};
  const { testSessionId = null } = createScheduledTester;

  // Used to get the remapped timezone name
  const {
    data: dataTimezoneName = {},
    loading: loadingTimezone,
  } = useQuery(TIMEZONE_NAME, {
    variables: {
      unix: timeZone,
    },
    skip: !timeZone,
  });

  const { timezoneName = '' } = dataTimezoneName;

  useEffect(() => {
    if (!profile) {
      onSignOut();
    }
  }, [profile, onSignOut]);

  useEffect(() => {
    if (contentRef.current && !loadingTimezone) {
      contentRef.current.focus();
    }
  }, [contentRef, loadingTimezone]);

  const keepOriginal = !_.isEmpty(originalTestSession) && !forceReschedule;
  const dateFormatted = keepOriginal ? originalTestSession.startAt : date;
  const dateWithTz = momentTimezone(dateFormatted).tz(timeZone);

  return (
    <Container tabIndex="0" role="none" ref={contentRef}>
      {dateWithTz && (
        <Card>
          <Content>
            {forceReschedule ? (
              <Title as="h1">{RESCHEDULED_MESSAGE}</Title>
            ) : (
              <Title as="h1">{keepOriginal ? NO_RESCHEDULED_MESSAGE : phrases[LIVE_TEXT_LABEL]}</Title>
            )}
            {!keepOriginal && (
              <InvitationText>{phrases[INVITATION_TEXT_LABEL]}</InvitationText>
            )}

            <DateContainer>
              {dateWithTz.format(FORMAT_DATE)}
            </DateContainer>
            <DateContainer>
              {`${dateWithTz.format(FORMAT_TIME)}, ${timezoneName}`}
            </DateContainer>

            {scope === AUTHENTICATION_SCOPES.TESTER && (
              <Link to={{
                pathname: '/',
                state: {
                  projectId,
                  testSessionId,
                },
              }}
              >
                Go back to Browse Tests
              </Link>
            )}
          </Content>
        </Card>
      )}
    </Container>
  );
};

Summary.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      date: PropTypes.string,
      timezone: PropTypes.string,
      forceReschedule: PropTypes.bool,
      originalTestSession: PropTypes.object,
      projectId: PropTypes.string,
    }),
    data: PropTypes.object,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  authentication: PropTypes.shape({
    scope: PropTypes.string.isRequired,
    profile: PropTypes.object,
  }).isRequired,
  phrases: PropTypes.shape({
    [LIVE_TEXT_LABEL]: PropTypes.string,
    [INVITATION_TEXT_LABEL]: PropTypes.string,
  }).isRequired,
  onSignOut: PropTypes.func.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  authentication,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  onSignOut: signOut,
}, dispatch);

export default compose(withTranslation, connect(mapStateToProps, mapDispatchToProps))(Summary);
